import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useCreateStore } from '@/stores/create';
import AuthService from '@/services/authService';
import SignatureService from '@/services/signatureService';
import { SigneeType } from '@/types/enums/SigneeType';
import type { Signee } from '@/types/Signee';
import type {
  DraftParticipant,
  ParticipantsDraftSnapshot,
  ParticipantsValidationResult
} from '@/types/create/WorkflowDraft';
import { EMAIL_REGEX } from '@deepcloud/field-validators';

function createTempId(): string {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function signeesToDraftParticipants(
  signeesOrdered: Array<Array<Signee>>
): DraftParticipant[] {
  const result: DraftParticipant[] = [];
  signeesOrdered.forEach((group, groupIndex) => {
    group.forEach((signee) => {
      result.push({
        tempId: createTempId(),
        signeeId: signee.signeeId,
        email: signee.email,
        type: signee.signeeType,
        groupIndex
      });
    });
  });
  return result;
}

export const useParticipantsDraft = defineStore('participantsDraft', () => {
  const createStore = useCreateStore();

  const ordered = ref(false);
  const userIsSigning = ref(false);
  const signeeAttachmentsAllowed = ref(false);
  const participants = ref<DraftParticipant[]>([]);
  const invalidTempIds = ref<string[]>([]);

  const initialSnapshot = ref<ParticipantsDraftSnapshot | null>(null);

  const hasSignees = computed(() =>
    participants.value.some((p) => p.type === SigneeType.SIGN)
  );

  const hasApprovers = computed(() =>
    participants.value.some((p) => p.type === SigneeType.APPROVE)
  );

  const signeeParticipants = computed(() =>
    participants.value.filter((p) => p.type === SigneeType.SIGN)
  );

  const isDirty = computed(() => {
    if (!initialSnapshot.value) return false;
    return (
      comparableSnapshot(buildComparableSnapshot()) !==
      comparableSnapshot(initialSnapshot.value)
    );
  });

  const canEnableAttachments = computed(() => {
    if (!createStore.hasSignees) {
      return false;
    }

    if (
      createStore.ordered &&
      createStore.hasApprovers &&
      createStore.hasSignees
    ) {
      const firstSignGroup = createStore.document?.signeesOrdered.find(
        (group) => group.some((signee) => signee.signeeType === SigneeType.SIGN)
      );
      const signeesInFirstGroup =
        firstSignGroup?.filter(
          (signee) => signee.signeeType === SigneeType.SIGN
        ).length ?? 0;
      if (signeesInFirstGroup > 1) {
        return false;
      }
    }

    if (!createStore.ordered && createStore.filteredSignees.length > 1) {
      return false;
    }

    return true;
  });

  function comparableSnapshot(
    snapshotValue: ParticipantsDraftSnapshot
  ): string {
    return JSON.stringify({
      ordered: snapshotValue.ordered,
      signeeAttachmentsAllowed: snapshotValue.signeeAttachmentsAllowed,
      participants: snapshotValue.participants.map((p) => ({
        signeeId: p.signeeId,
        email: p.email,
        type: p.type,
        groupIndex: p.groupIndex
      }))
    });
  }

  function buildComparableSnapshot(): ParticipantsDraftSnapshot {
    const doc = createStore.document;
    if (!doc) {
      return snapshot();
    }

    return {
      ordered: createStore.ordered,
      userIsSigning: userIsSigningFromStore(),
      signeeAttachmentsAllowed: signeeAttachmentsAllowed.value,
      participants: signeesToDraftParticipants(doc.signeesOrdered)
    };
  }

  function userIsSigningFromStore(): boolean {
    if (!AuthService.isAuthenticated) return false;
    return createStore.filteredSignees.some((signee) =>
      SignatureService.compareString(
        signee.email ?? '',
        AuthService.user?.email ?? ''
      )
    );
  }

  function snapshot(): ParticipantsDraftSnapshot {
    return {
      ordered: ordered.value,
      userIsSigning: userIsSigning.value,
      signeeAttachmentsAllowed: signeeAttachmentsAllowed.value,
      participants: participants.value.map((p) => ({ ...p }))
    };
  }

  function initFromDocument() {
    const doc = createStore.document;
    if (!doc) return;

    ordered.value = createStore.ordered;

    signeeAttachmentsAllowed.value = !!doc.signeeAttachmentsAllowed;

    const flatSignees = doc.signeesOrdered.flat();
    if (flatSignees.length === 0) {
      participants.value = [];
    } else {
      participants.value = signeesToDraftParticipants(doc.signeesOrdered);
    }

    userIsSigning.value = userIsSigningFromStore();

    invalidTempIds.value = [];
    initialSnapshot.value = snapshot();
  }

  function discard() {
    if (!initialSnapshot.value) return;
    ordered.value = initialSnapshot.value.ordered;
    userIsSigning.value = initialSnapshot.value.userIsSigning;
    signeeAttachmentsAllowed.value =
      initialSnapshot.value.signeeAttachmentsAllowed;
    participants.value = initialSnapshot.value.participants.map((p) => ({
      ...p
    }));
    invalidTempIds.value = [];
  }

  // Empty fields are allowed through — the participants dialog can be closed
  // with unfilled invitee rows prepared for later, the same way the observers
  // dialog allows saving with blank rows (see ObserversDialog.vue). Only a
  // NON-empty, malformed email blocks confirm; whether the document actually
  // has any real signee at all is enforced later, when the user tries to send
  // invitations (DocumentDrawer's validateInviteeForm), not here.
  function validate(): ParticipantsValidationResult {
    invalidTempIds.value = [];
    const flat = createStore.signeesOrderedFlat;
    const allValid = flat.every(
      (signee) => !signee.email || EMAIL_REGEX.test(signee.email)
    );

    return {
      valid: allValid,
      invalidTempIds: []
    };
  }

  function clearInvalidTempId(tempId: string) {
    invalidTempIds.value = invalidTempIds.value.filter((id) => id !== tempId);
  }

  async function reconcileAttachments() {
    const doc = createStore.document;
    if (!doc) return;

    if (signeeAttachmentsAllowed.value !== doc.signeeAttachmentsAllowed) {
      await createStore.updateAttachmentsAllow(signeeAttachmentsAllowed.value);
    }

    initialSnapshot.value = buildComparableSnapshot();
    invalidTempIds.value = [];
  }

  function addSignatory() {
    const nextGroup = ordered.value
      ? Math.max(...participants.value.map((p) => p.groupIndex), -1) + 1
      : 0;
    participants.value.push({
      tempId: createTempId(),
      email: null,
      type: SigneeType.SIGN,
      groupIndex: nextGroup
    });
  }

  function addApprover() {
    participants.value.unshift({
      tempId: createTempId(),
      email: null,
      type: SigneeType.APPROVE,
      groupIndex: 0
    });
    if (ordered.value) {
      participants.value.forEach((p, index) => {
        if (index > 0 && p.type === SigneeType.SIGN) {
          p.groupIndex += 1;
        }
      });
    }
  }

  function removeParticipant(tempId: string) {
    participants.value = participants.value.filter((p) => p.tempId !== tempId);
    if (participants.value.length === 0) {
      addSignatory();
    }
    syncUserIsSigningFromParticipants();
  }

  function setEmail(tempId: string, email: string | null) {
    const participant = participants.value.find((p) => p.tempId === tempId);
    if (participant) {
      participant.email = email;
      clearInvalidTempId(tempId);
    }
  }

  function setOrdered(value: boolean) {
    if (value === ordered.value) return;

    if (value) {
      participants.value = participants.value.map((p, index) => ({
        ...p,
        groupIndex: index
      }));
    } else {
      participants.value = participants.value.map((p) => ({
        ...p,
        groupIndex: 0
      }));
    }
    ordered.value = value;

    if (!canEnableAttachments.value && signeeAttachmentsAllowed.value) {
      signeeAttachmentsAllowed.value = false;
    }
  }

  function setSigneeAttachmentsAllowed(value: boolean) {
    signeeAttachmentsAllowed.value = value;
  }

  function syncUserIsSigningFromParticipants() {
    if (!AuthService.isAuthenticated) {
      userIsSigning.value = false;
      return;
    }
    userIsSigning.value = participants.value.some(
      (p) =>
        p.type === SigneeType.SIGN &&
        SignatureService.compareString(
          p.email ?? '',
          AuthService.user?.email ?? ''
        )
    );
  }

  function toggleUserSigning(value: boolean) {
    if (!AuthService.isAuthenticated || !AuthService.user?.email) return;

    userIsSigning.value = value;
    const userEmail = AuthService.user.email;

    if (value) {
      const existing = participants.value.find(
        (p) =>
          p.type === SigneeType.SIGN &&
          SignatureService.compareString(p.email ?? '', userEmail)
      );
      if (existing) {
        if (ordered.value) {
          existing.groupIndex = 0;
        }
        return;
      }

      const emptySign = participants.value.find(
        (p) => p.type === SigneeType.SIGN && !p.email
      );
      if (emptySign) {
        emptySign.email = userEmail;
      } else {
        participants.value.unshift({
          tempId: createTempId(),
          email: userEmail,
          type: SigneeType.SIGN,
          groupIndex: 0
        });
      }
    } else {
      participants.value = participants.value.filter(
        (p) =>
          !(
            p.type === SigneeType.SIGN &&
            SignatureService.compareString(p.email ?? '', userEmail)
          )
      );
      if (participants.value.length === 0) {
        addSignatory();
      }
    }
  }

  return {
    ordered,
    userIsSigning,
    signeeAttachmentsAllowed,
    participants,
    invalidTempIds,
    hasSignees,
    hasApprovers,
    signeeParticipants,
    isDirty,
    canEnableAttachments,
    initFromDocument,
    discard,
    validate,
    reconcileAttachments,
    addSignatory,
    addApprover,
    removeParticipant,
    setEmail,
    setOrdered,
    setSigneeAttachmentsAllowed,
    toggleUserSigning,
    clearInvalidTempId
  };
});
